import { DatePipe } from "@angular/common";
import { Injectable } from "@angular/core";
import { DynamicProperty, DYNAMIC_PROPERTY } from "../_objects/dynamic-property";
import { InspectionLocation, Inspector } from "../_objects/inspection";
import { InspectionReport, ReportLocation } from "../_objects/inspection-report";
import { MarkItem } from "../_objects/mark";
import { SubjectLocation } from "../_objects/subject-model";
import { OFFLINE_REPORT_PREVIEW } from "../_offline/electron.constants";
import { IndividualReportTemplateSchema } from "../_offline/models/individual-report-template.schema";
import { IndividualReportSchema } from "../_offline/models/individual-report.schema";
import { InspectionSchema } from "../_offline/models/inspection.schema";
import { InspectionImageSchema } from "../_offline/models/inspectionImage.schema";
import {
  TemplateDefinition,
  TemplateText,
  TemplatePageSection,
  EditableTextList,
  TableColumn
} from "../_offline/models/report-preview/report-template";
import { ImageComponent } from "../_offline/models/report-preview/TableData";
import { TenantDetailSchema } from "../_offline/models/tenant-detail.schema";
import { TenantConfigSchema } from "../_offline/models/tenantConfiguration.schema";
import { UserSchema } from "../_offline/models/user.schema";
import { ViewDataModel } from "../_offline/models/report-preview/view-data-model";
import { RADIO_DISPLAY, TEXT_AREA_INPUT_TYPE, TextComponent } from "../_offline/models/report-preview/report-text";

@Injectable({
  providedIn: "root"
})
export class ReportPreviewContentService {
  readonly defaultDateFormat = "yyyy/MM/dd";
  private _templateSchema: IndividualReportTemplateSchema;
  private _inspectionSchema: InspectionSchema;
  private _imageSchemas: InspectionImageSchema[];
  private _reportSchema: IndividualReportSchema;
  private _configSchemas: TenantConfigSchema[];
  private _userSchemas: UserSchema[];
  private _tenantSchema: TenantDetailSchema;

  public reportLocationsWithDummy: ReportLocation[];

  get templateSchema() {
    return this._templateSchema;
  }
  get inspectionSchema() {
    return this._inspectionSchema;
  }
  get imageSchemas() {
    return this._imageSchemas;
  }

  get reportSchema() {
    return this._reportSchema;
  }
  get configSchemas() {
    return this._configSchemas;
  }
  get userSchemas() {
    return this._userSchemas;
  }
  get tenantSchema() {
    return this._tenantSchema;
  }

  constructor(private datePipe: DatePipe) {}

  setData(
    templateSchema: IndividualReportTemplateSchema,
    inspectionSchema: InspectionSchema,
    imageSchemas: InspectionImageSchema[],
    reportSchema: IndividualReportSchema,
    configSchemas: TenantConfigSchema[],
    userSchemas: UserSchema[],
    tenantSchema: TenantDetailSchema
  ) {
    this._templateSchema = templateSchema;
    this._inspectionSchema = inspectionSchema;
    this._imageSchemas = imageSchemas;
    this._reportSchema = reportSchema;
    this._configSchemas = configSchemas;
    this._userSchemas = userSchemas;
    this._tenantSchema = tenantSchema;
  }

  getTexts(templateJson: string, type: string) {
    const replacedTemplate = templateJson
      .replace(/(\r\n)/g, "\n")
      .replace(/(\r)/g, "\n")
      .replace(/(\n)/g, "\\n");

    let template: TemplateDefinition;

    try {
      template = JSON.parse(replacedTemplate) as TemplateDefinition;
    } catch {
      template = JSON.parse(templateJson) as TemplateDefinition;
    }

    const headerEditableTexts = this.getTextsList(template.header.texts, type);
    const bodiesEditableTexts = this.getBodiesTexts(template.bodies, type);
    const footerEditableTexts = this.getTextsList(template.footer.texts, type);

    return headerEditableTexts.concat(bodiesEditableTexts).concat(footerEditableTexts);
  }
  getTextsList(texts: TemplateText[], type: string) {
    return texts
      .filter((text) => text.type === type)
      .map((text) => new TextComponent(text.dynamicId, text.text, text.editableId, text.inputType, text.radioType, text.multiSelectDelimiter));
  }

  getBodiesTexts(bodies: TemplatePageSection[], type: string) {
    const bodiesEditableTextsList = bodies.map((body) => body.texts).map((texts) => this.getTextsList(texts, type));

    let bodiesEditableTexts = [];
    bodiesEditableTextsList.forEach((texts) => (bodiesEditableTexts = bodiesEditableTexts.concat(texts)));
    return bodiesEditableTexts;
  }

  getDynamicEditableTexts(
    templateJson: string,
    report: IndividualReportSchema,
    inspection: InspectionSchema,
    currentUser: UserSchema,
    users: UserSchema[],
    tenantName: string,
    viewData: ViewDataModel
  ) {
    const replacedTemplate = templateJson
      .replace(/(\r\n)/g, "\n")
      .replace(/(\r)/g, "\n")
      .replace(/(\n)/g, "\\n");
    let template: TemplateDefinition;

    try {
      template = JSON.parse(replacedTemplate) as TemplateDefinition;
    } catch {
      template = JSON.parse(templateJson) as TemplateDefinition;
    }

    const dictionary = {};
    this.getDynamicEditableTextsList(template.header.texts, dictionary, report, inspection, currentUser, users, tenantName, viewData);
    this.getBodiesDynamicEditableTexts(template.bodies, dictionary, report, inspection, currentUser, users, tenantName, viewData);
    this.getDynamicEditableTextsList(template.footer.texts, dictionary, report, inspection, currentUser, users, tenantName, viewData);

    return dictionary;
  }
  getDynamicEditableTextsList(
    texts: TemplateText[],
    dictionary: any,
    report: IndividualReportSchema,
    inspection: InspectionSchema,
    currentUser: UserSchema,
    users: UserSchema[],
    tenantName: string,
    viewData: ViewDataModel
  ) {
    return texts?.forEach((text) => {
      if (text?.editableId) {
        const component = new TextComponent(text.dynamicId, text.text, text.editableId, text.inputType, text.radioType, text.multiSelectDelimiter);

        if (text.isTableText) {
          viewData.summaryData.forEach((data) => {
            const value = this.getEditableContent(
              component,
              text.format,
              report,
              inspection,
              currentUser,
              users,
              tenantName,
              null,
              null,
              null,
              "",
              data.tableData.id
            );
            dictionary[text.editableId + "_" + data.tableData.id + (viewData.isAllDataMode ? "_" + data.imageId : "")] = value;
          });
        } else {
          const value = this.getEditableContent(component, text.format, report, inspection, currentUser, users, tenantName);
          dictionary[text.editableId] = value;
        }
      }
    });
  }

  getBodiesDynamicEditableTexts(
    bodies: TemplatePageSection[],
    dictionary: any,
    report: IndividualReportSchema,
    inspection: InspectionSchema,
    currentUser: UserSchema,
    users: UserSchema[],
    tenantName: string,
    viewData: ViewDataModel
  ) {
    bodies
      .map((body) => body.texts)
      .map((texts) => this.getDynamicEditableTextsList(texts, dictionary, report, inspection, currentUser, users, tenantName, viewData));
    bodies.map((body) =>
      body.inspectionSummary?.table?.columns?.forEach((column) =>
        this.getDynamicEditableTextsList(column.textsInfo, dictionary, report, inspection, currentUser, users, tenantName, viewData)
      )
    );
  }

  public genEditableText(text: TemplateText) {
    const result = new EditableTextList();
    result.editableId = text.editableId;
    result.inputGroup = text.inputGroup;
    result.label = text.label;
    result.radioLabel = text.radioLabel;
    result.value = text.value;
    result.text = text.text;
    result.validation = text.validation;
    result.options = text.options;
    result.inputGroup = text.inputGroup;
    return result;
  }

  public getContent(
    dynamicId: string,
    defaultText: string,
    format: string,
    report: IndividualReportSchema,
    inspection: InspectionSchema,
    currentUser: UserSchema,
    users: UserSchema[],
    tenantName: string,
    pageNumber?: number,
    total?: number,
    images?: ImageComponent[],
    locationId?: string
  ) {
    let content: string = null;
    const property = new DynamicProperty(dynamicId);
    const inspectors = JSON.parse(inspection.inspectors) as Inspector[];

    const subjectModelLocations = inspection.subject.subjectModel.subjectModelLocations;
    const subjectModelLocation = subjectModelLocations.find((_) => _.version === inspection.locationVersion);
    const locations = JSON.parse(subjectModelLocation.locations) as SubjectLocation[];
    const inspectionLocations = JSON.parse(inspection.inspectionLocations) as InspectionLocation[];

    switch (property.key) {
      case DYNAMIC_PROPERTY.KEY.PageNumber:
        content = this.getPageNumber(format, pageNumber, total);
        break;

      case DYNAMIC_PROPERTY.KEY.TotalPageNumber:
        content = total?.toString();
        break;

      case DYNAMIC_PROPERTY.KEY.TenantName:
        content = tenantName;
        break;

      case DYNAMIC_PROPERTY.KEY.InspectionReportTitle:
        content = report.title;
        break;

      case DYNAMIC_PROPERTY.KEY.InspectionReportRevision:
        content = report.version !== null ? report.version.toString() : "";
        break;

      case DYNAMIC_PROPERTY.KEY.InspectionReportOwner:
        content = currentUser?.givenName + " " + currentUser?.surname;
        break;

      case DYNAMIC_PROPERTY.KEY.InspectionInspectionDate:
        if (property.index < 1) {
          content = this.formatDateTime(inspection.inspectionDate, format);
        }
        break;

      case DYNAMIC_PROPERTY.KEY.InspectionSubjectSerialNo:
        if (property.index < 1) {
          content = inspection.subject.serialNo;
        }
        break;

      case DYNAMIC_PROPERTY.KEY.InspectionSubjectModelName:
        content = inspection.subject.subjectModel.name;
        break;

      case DYNAMIC_PROPERTY.KEY.InspectionSubjectOwner:
        if (property.index < 1) {
          content = inspection.subject.owner;
        }
        break;
      case DYNAMIC_PROPERTY.KEY.InspectionSubjectCountry:
        if (property.index < 1) {
          content = inspection.subject.country;
        }
        break;
      case DYNAMIC_PROPERTY.KEY.InspectionSubjectState:
        if (property.index < 1) {
          content = inspection.subject.state;
        }
        break;
      case DYNAMIC_PROPERTY.KEY.InspectionSubjectDistrict:
        if (property.index < 1) {
          content = inspection.subject.district;
        }
        break;
      case DYNAMIC_PROPERTY.KEY.InspectionSubjectSite:
        if (property.index < 1) {
          content = inspection.subject.site;
        }
        break;
      case DYNAMIC_PROPERTY.KEY.InspectionSubjectProject:
        if (property.index < 1) {
          content = inspection.subject.project;
        }
        break;

      case DYNAMIC_PROPERTY.KEY.InspectionLocationIdentifierComment:
        content = inspectionLocations.find((loc) => loc.identifier === locationId)?.comment;
        break;

      case DYNAMIC_PROPERTY.KEY.InspectionInspectorsName:
        if (property.index < 1) {
          content = users
            .filter((user) => inspectors.some((i) => user.id === i.id))
            .map((user) => user.givenName + " " + user.surname)
            .filter((name) => name !== "" && name !== null)
            .join(",");
        }
        break;

      case DYNAMIC_PROPERTY.KEY.InspectionInspectorsInitials:
        if (property.index < 1) {
          content = users
            .filter((user) => inspectors.some((i) => user.id === i.id))
            .map((user) => user.givenName + " " + user.surname)
            .map((name) => this.getInitials(name))
            .filter((name) => name !== "" && name !== null)
            .join(",");
        }
        break;

      case DYNAMIC_PROPERTY.KEY.InspectionInspectorsEmail:
        if (property.index < 1) {
          content = users
            .filter((user) => inspectors.some((i) => user.id === i.id))
            .map((user) => user.email)
            .join(",");
        }
        break;

      case DYNAMIC_PROPERTY.KEY.InspectionInspectorsTelephoneNumber:
        if (property.index < 1) {
          content = users
            .filter((user) => inspectors.some((i) => user.id === i.id))
            .map((user) => user.telephoneNumber)
            .join(",");
        }
        break;

      case DYNAMIC_PROPERTY.KEY.SystemNow:
        const today = new Date();
        content = this.formatDateTime(today.toString(), format);
        break;

      case DYNAMIC_PROPERTY.KEY.DataPlateLocationName:
        content = this.getLocation(locations, defaultText)?.name;
        break;

      case DYNAMIC_PROPERTY.KEY.InspectionPicturesLocationUppermostName:
        const reportLocations = this.reportLocationsWithDummy;

        content = "";
        if (!images) {
          break;
        }

        const contentList = [];
        // imageIdsが含まれているreportLocationのreportLocations.identifierから、location構造の親ロケーション名を取得する
        images.forEach((image) => {
          return reportLocations.some((reportLocation) => {
            if (reportLocation.imageIds.includes(image.id)) {
              return locations.some((loc) => {
                const target = this.hasLocation(loc, reportLocation?.identifier);
                if (target) {
                  contentList.push(loc.name);
                  return true;
                }
              });
            }
          });
        });

        Array.from(new Set(contentList)).map((value, index) => {
          if (index > 0) {
            content += " / ";
          }

          content += value;
        });
        break;

      default:
        break;
    }

    return content === null || content === "" ? "" : content;
  }

  public getColumnHeaderContent(column: TableColumn, marks: MarkItem[]) {
    let content: string = null;

    switch (column.headerType) {
      case "dynamic":
        const property = new DynamicProperty(column.header);
        switch (property.key) {
          case DYNAMIC_PROPERTY.KEY.InspectionMarkLabel:
            if (marks) {
              const mark = marks.find((m) => m.identifier === property.index);
              if (mark !== null) {
                content = mark?.label;
              }
            }
            break;
          default:
            content = column.header;
            break;
        }
        break;
      case "static":
      default:
        content = column.header;
        break;
    }

    return content !== null && content !== "" ? content : "";
  }

  public getInitials(name: string): string {
    if (!name || name === "") {
      return "";
    }

    const words: Array<string> = name.split(/\s/).filter(function (v) {
      return v !== "";
    });
    let initials: string;

    if (words.length > 1) {
      initials = words[0].slice(0, 1) + words[words.length - 1].slice(0, 1);
    }

    if (words.length === 1) {
      initials = words[0].slice(0, 1);
    }

    return initials.toUpperCase();
  }

  private getLocation(locations: SubjectLocation[], identifier: string) {
    let result: SubjectLocation = null;
    if (locations != null) {
      locations.some((location) => {
        if (location.identifier === identifier) {
          result = location;
          return true;
        }

        if (location.children.length > 0) {
          const subjectModelLocation = this.getLocation(location.children, identifier);
          if (subjectModelLocation !== null) {
            result = subjectModelLocation;
            return true;
          }
        }

        return false;
      });
    }

    return result;
  }

  private hasLocation(location: SubjectLocation, identifier: string) {
    if (location?.identifier === identifier) {
      return true;
    }

    const child = this.getLocation(location?.children, identifier);
    if (child) {
      return true;
    }

    return false;
  }

  private formatDateTime(dateTime: string, format: string) {
    if (dateTime === null || dateTime === "") {
      return "";
    }

    if (format === null || format === "") {
      format = this.defaultDateFormat;
    }

    // 曜日フォーマットの置換
    format = format.replace(/dddd/, "EEEE");
    format = format.replace(/ddd/, "EEE");

    return this.datePipe.transform(dateTime, format);
  }

  public getEditableContent(
    editableText: TextComponent,
    format: string,
    report: IndividualReportSchema,
    inspection: InspectionSchema,
    currentUser: UserSchema,
    users: UserSchema[],
    tenantName: string,
    pageNumber?: number,
    total?: number,
    mode: number = OFFLINE_REPORT_PREVIEW.FormatMode.Display,
    addionalEditableId: string = "",
    locationId?: string
  ) {
    if (editableText.editableId === null) {
      return null;
    }

    // The editable id is prior to the the dynamic id.
    // Convert EditableTexts Key to LowerCamelCase
    const editableId = editableText.editableId.substring(0, 1).toLowerCase() + editableText.editableId.substring(1) + addionalEditableId;

    const editableTexts = JSON.parse(report.editableTexts);

    let content = editableTexts[editableId];

    // No value of this editable text. (The inspection report has no value of the editable id.)
    if (content == null) {
      if (editableText.dynamicId != null) {
        content = this.getContent(
          editableText.dynamicId,
          editableText.text,
          format,
          report,
          inspection,
          currentUser,
          users,
          tenantName,
          pageNumber,
          total,
          null,
          locationId
        );
      } else {
        content = editableText.text;
      }
    }

    switch (editableText.inputType) {
      case TEXT_AREA_INPUT_TYPE.Radio:
        if (content === editableText.value) {
          content = mode === OFFLINE_REPORT_PREVIEW.FormatMode.Display ? RADIO_DISPLAY.RadioType["check"] : content;
        } else {
          content = "";
        }
        break;

      case TEXT_AREA_INPUT_TYPE.Date:
        if (content === null || content === "") {
          return "";
        }

        if (format === null || format === "") {
          format = this.defaultDateFormat;
        }

        const date = new Date(content);
        // contentが無効な値だった場合、getTime()の値がNanとなる
        if (Number.isNaN(date.getTime())) {
          return "";
        }

        const dateStr = date.toISOString();
        // 曜日フォーマットの置換
        format = format.replace(/dddd/, "EEEE");
        format = format.replace(/ddd/, "EEE");

        content = this.formatDateTime(dateStr, format);
        break;
      case TEXT_AREA_INPUT_TYPE.MultiSelect:
        if (editableText.multiSelectDelimiter === "newline" && content) {
          // string.replaceは最初に見つかった1文字のみを置き換えしますので、正規表現を使う必要があります。
          content = content.replace(/,/g, "\r\n");
        }
        break;
      default:
        break;
    }

    return content;
  }

  genReportSchema(report: InspectionReport) {
    const schema = new IndividualReportSchema();
    schema.reportId = report.reportId;
    schema.version = report.version;
    schema.isNewestVersion = true;
    schema.id = report.id;
    schema.inspectionId = report.inspectionIds[0];

    schema.status = report.status;
    schema.templateId = report.templateId;
    schema.blobStorageAccount = null;
    schema.schemaVersion = report.schemaVersion;
    schema.type = "individual";
    schema.isAutoInputTitle = report.isAutoInputTitle;
    schema.isValid = true;
    schema.isDeleted = false;

    schema.createdBy = report.createdBy;
    schema.lastUpdatedBy = report.lastUpdatedBy;
    schema.createDate = report.createDate;
    schema.lastUpdateDate = report.lastUpdateDate;
    schema.tenantId = this.tenantSchema?.id;
    schema.title = report.title;
    schema.reportLocations = JSON.stringify(report.reportLocations);
    schema.editableTexts = JSON.stringify(report.editableTexts);
    schema.excludedInputGroups = JSON.stringify(report.excludedInputGroups);
    return schema;
  }

  public getPageNumber(format: string, pageCount: number, totalPages: number) {
    return format.replace("$", pageCount?.toString()).replace("#", totalPages?.toString());
  }
}
